<template>
  <div class="dashboard-locked">
    <div class="dashboard-locked-preview" aria-hidden="true">
      <div class="locked-meta">
        <div>
          <span class="locked-meta-label">Stats</span>
          <strong>2m ago</strong>
        </div>
        <div class="locked-range">
          <span>Range</span>
          <span
            v-for="range in ['7d', '3m', '6m', '1y']"
            :key="range"
            :class="{ active: range === '7d' }">
            {{ range }}
          </span>
        </div>
      </div>

      <div class="locked-grid locked-grid-two">
        <div
          v-for="server in servers"
          :key="server.name"
          class="locked-card locked-status-card"
          :class="'locked-status-' + server.key">
          <div class="locked-status-content">
            <div>
              <span class="locked-label">{{ server.name }}</span>
              <strong class="locked-status-value">{{ server.players }}</strong>
              <span class="locked-subtitle">Players online</span>
            </div>
            <i class="icon-settings"></i>
          </div>
          <LockedChart :variant="server.key"/>
        </div>
      </div>

      <div class="locked-grid locked-grid-three">
        <div
          v-for="stat in stats"
          :key="stat.label"
          class="locked-card locked-stat-card"
          :class="'locked-stat-' + stat.variant">
          <span class="locked-label">{{ stat.label }}</span>
          <strong class="locked-stat-value">{{ stat.value }}</strong>
          <span class="locked-subtitle">{{ stat.subtitle }}</span>
          <LockedChart :variant="stat.variant" compact/>
        </div>
      </div>

      <div class="locked-grid locked-grid-three">
        <div v-for="list in lists" :key="list.title" class="locked-card locked-list-card">
          <div class="locked-card-header">
            <strong>
              <i :class="list.icon"></i>
              {{ list.title }}
            </strong>
            <span class="locked-server-pill">All</span>
          </div>
          <div v-for="row in 5" :key="list.title + '-' + row" class="locked-list-row">
            <span class="locked-avatar">{{ row }}</span>
            <span class="locked-list-copy">
              <strong>{{ mockNames[row - 1] }}</strong>
              <small>{{ 1200 + row * 347 }}h Playtime</small>
            </span>
            <span class="locked-list-value">
              <small>{{ list.valueLabel }}</small>
              <strong>{{ list.values[row - 1] }}</strong>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-lock-shade"></div>
    <div class="dashboard-lock-panel">
      <span class="dashboard-lock-icon">
        <i class="fa fa-lock" aria-hidden="true"></i>
      </span>
      <div>
        <strong>Dashboard locked</strong>
        <p>Dashboard View permission is required to view live statistics.</p>
      </div>
    </div>
  </div>
</template>

<script>
const LockedChart = {
  functional: true,
  props: {
    variant: {
      type: String,
      default: 'teal'
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  render (createElement, context) {
    const points = context.props.compact
      ? '0,42 12,35 25,38 38,24 52,29 66,16 80,22 94,9 110,15 126,7'
      : '0,50 14,44 28,47 43,31 58,36 73,24 88,29 104,13 120,19 140,8'
    return createElement('svg', {
      class: ['locked-chart', `locked-chart-${context.props.variant}`],
      attrs: {
        viewBox: context.props.compact ? '0 0 126 52' : '0 0 140 58',
        preserveAspectRatio: 'none'
      }
    }, [
      createElement('line', { attrs: { x1: '0', y1: '46', x2: '140', y2: '46' } }),
      createElement('line', { attrs: { x1: '0', y1: '27', x2: '140', y2: '27' } }),
      createElement('polyline', { attrs: { points } })
    ])
  }
}

export default {
  name: 'DashboardLocked',
  components: {
    LockedChart
  },
  data () {
    return {
      servers: [
        { name: 'Silver', key: 'silver', players: '1,438' },
        { name: 'Gold', key: 'gold', players: '987' }
      ],
      stats: [
        { label: 'Active players', value: '18,642', subtitle: 'Silver 10,421 · Gold 8,221', variant: 'teal' },
        { label: 'New players', value: '1,284', subtitle: 'All servers', variant: 'blue' },
        { label: 'Average money', value: '$246,891', subtitle: 'Silver $258k · Gold $235k', variant: 'amber' }
      ],
      mockNames: ['TrainerNova', 'CrystalPeak', 'NightSignal', 'SilverEcho', 'AuroraVale'],
      lists: [
        {
          title: 'Top Money',
          icon: 'fa fa-money',
          valueLabel: 'Money',
          values: ['$98.4m', '$86.2m', '$79.7m', '$72.1m', '$68.5m']
        },
        {
          title: 'Ranked Wins',
          icon: 'fa fa-trophy',
          valueLabel: 'Wins',
          values: ['2,481', '2,216', '1,984', '1,822', '1,706']
        },
        {
          title: 'Ranked Losses',
          icon: 'fa fa-times-circle',
          valueLabel: 'Losses',
          values: ['1,342', '1,247', '1,106', '982', '911']
        }
      ]
    }
  }
}
</script>

<style scoped>
.dashboard-locked {
  --locked-border: #23282c;
  --locked-surface: #3a4148;
  position: relative;
  min-height: 690px;
  overflow: hidden;
}

.dashboard-locked-preview {
  filter: blur(3.5px);
  opacity: 0.62;
  pointer-events: none;
  user-select: none;
  transform: scale(1.008);
  transform-origin: top center;
}

.locked-meta {
  display: flex;
  min-height: 48px;
  padding: 9px 12px;
  margin-bottom: 12px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(15, 22, 28, 0.35);
  color: #d4e2e8;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.locked-meta-label,
.locked-range > span:first-child {
  margin-right: 7px;
  color: #7f9199;
}

.locked-range {
  display: flex;
  align-items: center;
  gap: 6px;
}

.locked-range span:not(:first-child),
.locked-server-pill {
  padding: 4px 8px;
  border: 1px solid #324047;
  border-radius: 4px;
  background: #2a343b;
  color: #9eb1ba;
  font-size: 0.7rem;
}

.locked-range span.active {
  background: #324047;
  color: #e6f0f2;
}

.locked-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;
}

.locked-grid-two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.locked-grid-three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.locked-card {
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--locked-border);
  border-radius: 2px;
  background: var(--locked-surface);
}

.locked-card::after {
  position: absolute;
  content: '';
  top: 0;
  right: 0;
  left: 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(0, 150, 136, 0.45), transparent 72%);
}

.locked-status-card {
  min-height: 156px;
  padding: 18px 18px 8px;
}

.locked-status-silver::after {
  background: linear-gradient(90deg, rgba(203, 211, 219, 0.7), transparent 72%);
}

.locked-status-gold::after {
  background: linear-gradient(90deg, rgba(230, 199, 146, 0.75), transparent 72%);
}

.locked-status-content {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.locked-status-content > div {
  display: flex;
  flex-direction: column;
}

.locked-status-content .icon-settings {
  color: #9aa4ad;
}

.locked-label {
  color: #8fa3ad;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.locked-status-value,
.locked-stat-value {
  display: block;
  margin: 5px 0 1px;
  color: #dbe5ea;
  font-size: 2rem;
  line-height: 1.1;
}

.locked-status-silver .locked-status-value {
  color: #cbd3db;
}

.locked-status-gold .locked-status-value {
  color: #e6c792;
}

.locked-subtitle {
  display: block;
  color: #9aa4ad;
  font-size: 0.77rem;
}

.locked-chart {
  width: 100%;
  height: 58px;
  margin-top: 8px;
  overflow: visible;
}

.locked-chart line {
  stroke: rgba(255, 255, 255, 0.055);
  stroke-width: 0.8;
}

.locked-chart polyline {
  fill: none;
  stroke: rgba(99, 194, 222, 0.75);
  stroke-width: 2;
  vector-effect: non-scaling-stroke;
}

.locked-chart-silver polyline {
  stroke: rgba(203, 211, 219, 0.8);
}

.locked-chart-gold polyline,
.locked-chart-amber polyline {
  stroke: rgba(230, 199, 146, 0.82);
}

.locked-chart-teal polyline {
  stroke: rgba(77, 189, 116, 0.78);
}

.locked-stat-card {
  min-height: 168px;
  padding: 18px 18px 7px;
}

.locked-stat-card .locked-stat-value {
  font-size: 1.8rem;
}

.locked-stat-blue::after {
  background: linear-gradient(90deg, rgba(99, 194, 222, 0.55), transparent 72%);
}

.locked-stat-amber::after {
  background: linear-gradient(90deg, rgba(246, 195, 67, 0.55), transparent 72%);
}

.locked-stat-card .locked-chart {
  height: 52px;
  margin-top: 10px;
}

.locked-list-card {
  padding: 0;
}

.locked-card-header {
  display: flex;
  min-height: 49px;
  padding: 13px 15px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--locked-border);
  background: #343b41;
  color: #d8dee4;
}

.locked-card-header i {
  width: 18px;
  margin-right: 5px;
  color: #63c2de;
}

.locked-list-row {
  display: flex;
  min-height: 63px;
  padding: 9px 14px;
  align-items: center;
  gap: 11px;
  border-bottom: 1px solid var(--locked-border);
  color: #d8dee4;
}

.locked-list-row:last-child {
  border-bottom: 0;
}

.locked-avatar {
  display: inline-flex;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #5f6c76, #30383f);
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.7rem;
}

.locked-list-copy,
.locked-list-value {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.locked-list-copy {
  flex: 1;
}

.locked-list-copy strong {
  overflow: hidden;
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.locked-list-copy small,
.locked-list-value small {
  color: #929da6;
  font-size: 0.66rem;
}

.locked-list-value {
  align-items: flex-end;
  text-align: right;
}

.locked-list-value strong {
  margin-top: 2px;
  padding: 2px 6px;
  border: 1px solid #59636b;
  border-radius: 3px;
  background: #2f353a;
  font-size: 0.75rem;
}

.dashboard-lock-shade {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at center, rgba(27, 36, 43, 0.12) 0, rgba(19, 25, 30, 0.52) 72%),
    rgba(19, 25, 30, 0.1);
}

.dashboard-lock-panel {
  position: absolute;
  top: 48%;
  left: 50%;
  display: flex;
  width: min(460px, calc(100% - 32px));
  padding: 18px 20px;
  align-items: center;
  gap: 15px;
  transform: translate(-50%, -50%);
  border: 1px solid rgba(99, 194, 222, 0.34);
  border-radius: 6px;
  background: rgba(34, 43, 50, 0.94);
  color: #dce7ec;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.38), 0 0 30px rgba(32, 168, 216, 0.08);
  backdrop-filter: blur(8px);
}

.dashboard-lock-panel strong {
  display: block;
  margin-bottom: 3px;
  font-size: 1.08rem;
}

.dashboard-lock-panel p {
  margin: 0;
  color: #aebbc2;
  font-size: 0.82rem;
  line-height: 1.45;
}

.dashboard-lock-icon {
  display: inline-flex;
  width: 50px;
  height: 50px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(99, 194, 222, 0.48);
  border-radius: 50%;
  background: rgba(32, 168, 216, 0.12);
  color: #63c2de;
  font-size: 1.35rem;
}

@media (max-width: 1199.98px) {
  .locked-grid-three {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .locked-list-card:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 767.98px) {
  .locked-grid-two,
  .locked-grid-three {
    grid-template-columns: 1fr;
  }

  .locked-list-card:last-child {
    grid-column: auto;
  }

  .locked-meta {
    align-items: flex-start;
    flex-direction: column;
  }

  .locked-range {
    width: 100%;
  }

  .dashboard-lock-panel {
    top: 260px;
  }
}

@media (max-width: 479.98px) {
  .dashboard-lock-panel {
    align-items: flex-start;
  }

  .dashboard-lock-icon {
    width: 42px;
    height: 42px;
  }
}
</style>
